import * as yup from 'yup';

const ONLY_NUMBERS = /^\d+$/;
const CEP_REGEX = /[0-9]{5}-[\d]{3}$/;
const CNPJ_REGEX = /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/;
const CPF_REGEX = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/;
const CUIT_CUIL_REGEX = /^\d{2}(\d){7,8}\d{1}$/;
const CUIT_CUIL_DNI_REGEX = /^\d{2}(\d){4,8}\d{1}$/;
const CURP_REGEX = /^[a-zA-Z0-9]{18}$/;
const RFC_PERSONA_MORAL_REGEX = /^(([A-Z,Ñ,&]|[a-z,ñ,&]){3})([0-9]{6})((([A-Z]|[a-z]|[0-9]){3}))$/;
const RFC_PERSONA_FISICA_REGEX = /^(([A-Z,Ñ,&]|[a-z,ñ,&]|\\s){1})(([A-Z]|[a-z]){3})([0-9]{6})((([A-Z]|[a-z]|[0-9]){3}))$/;
const DNI_PERU_REGEX = /^\d{8,9}$/;
const RUC_PERU_REGEX = /^\d{11}$/;
const CARD_EXPIRATION_REGEX = /^(0[1-9]|1[0-2])\s\/\s?([0-9]{4}|[0-9]{2})$/;
const THREE_CHAR_MIN = /^(?=(\W*\w){3}).*$/;

yup.setLocale({
  mixed: {
    required: value => `publisher.step4.module2.field.${value.path}.error`,
    oneOf: value => `publisher.step4.module2.field.${value.path}.error`,
    notOneOf: value => `publisher.step4.module2.field.${value.path}.error`
  },
  string: {
    matches: value => `publisher.step4.module2.field.${value.path}.error`,
    max: value => `publisher.step4.module2.field.${value.path}.error`,
    min: value => `publisher.step4.module2.field.${value.path}.error`,
    email: value => `publisher.step4.module2.field.${value.path}.error`,
    length: value => `publisher.step4.module2.field.${value.path}.error`
  }
});

const Step4Module2Validations = yup.object().shape({
  userName: yup.string().when(['$isBillingReadOnly', '$siteId'], (isBillingReadOnly, siteId, schema) => {
    const legalNameFormat = siteId === 'ZPAR' || siteId === 'DVPE' || siteId === 'UBPE' ? THREE_CHAR_MIN : '';
    return !isBillingReadOnly
      ? schema
          .max(40)
          .trim()
          .required()
          .matches(legalNameFormat, 'publisher.step4.module2.field.userName.error.format')
      : yup.string().notRequired();
  }),
  userTaxCondition: yup.string().when('$isBillingReadOnly', (isBillingReadOnly, schema) => {
    return !isBillingReadOnly ? schema.required() : yup.string().notRequired();
  }),
  userDocument: yup
    .string()
    .trim()
    .required()
    .when(
      ['$isBillingReadOnly', '$siteId', 'userTaxCondition'],
      (isBillingReadOnly, siteId, userTaxCondition, schema) => {
        if (!isBillingReadOnly) {
          if (siteId === 'ZPAR') {
            if (userTaxCondition === '13') {
              return schema
                .min(3, 'publisher.step4.module2.field.userDocument.error')
                .max(20, 'publisher.step4.module2.field.userDocument.error');
            }
            return schema.matches(
              userTaxCondition === '4' ? CUIT_CUIL_DNI_REGEX : CUIT_CUIL_REGEX,
              'publisher.step4.module2.field.userDocument.error.format'
            );
          }
          if (siteId === 'IWBR' || siteId === 'WIBR') {
            return schema.matches(userTaxCondition === '9' ? CNPJ_REGEX : CPF_REGEX);
          }
          if (siteId === 'DVPE' || siteId === 'UBPE') {
            if (userTaxCondition === '302' || userTaxCondition === '324') {
              return schema
                .transform(value => (value ? value.replace(/[\W]/g, '') : value))
                .max(11)
                .matches(ONLY_NUMBERS, 'publisher.step4.module2.field.userDocument.error.format')
                .matches(RUC_PERU_REGEX, 'publisher.step4.module2.field.userDocument.error');
            }
            if (userTaxCondition === '314' || userTaxCondition === '325') {
              return schema
                .transform(value => (value ? value.replace(/[\W]/g, '') : value))
                .max(11)
                .matches(ONLY_NUMBERS, 'publisher.step4.module2.field.userDocument.error.format')
                .matches(DNI_PERU_REGEX, 'publisher.step4.module2.field.userDocument.error');
            }
            return schema.max(11).matches(ONLY_NUMBERS, 'publisher.step4.module2.field.userDocument.error.format');
          }
          if (siteId === '24MX') {
            if (userTaxCondition === '6') {
              return schema
                .transform(value => (value ? value.replace(/[\W]/g, '') : value))
                .max(18)
                .matches(CURP_REGEX);
            }
            if (userTaxCondition === '301') {
              return schema
                .transform(value => (value ? value.replace(/[\W]/g, '') : value))
                .max(18)
                .matches(RFC_PERSONA_MORAL_REGEX);
            }
            return schema
              .transform(value => (value ? value.replace(/[\W]/g, '') : value))
              .max(18)
              .matches(RFC_PERSONA_FISICA_REGEX);
          }
          if (siteId === 'VAMX') {
            if (userTaxCondition === '1006') {
              return schema
                .transform(value => (value ? value.replace(/[\W]/g, '') : value))
                .max(18)
                .matches(CURP_REGEX);
            }
            if (userTaxCondition === '1301') {
              return schema
                .transform(value => (value ? value.replace(/[\W]/g, '') : value))
                .max(18)
                .matches(RFC_PERSONA_MORAL_REGEX);
            }
            return schema
              .transform(value => (value ? value.replace(/[\W]/g, '') : value))
              .max(18)
              .matches(RFC_PERSONA_FISICA_REGEX);
          }
        }
        return yup.string().notRequired();
      }
    ),
  address: yup
    .string()
    .when(['$isBillingReadOnly', '$shouldValidateLocation'], (isBillingReadOnly, shouldValidateLocation, schema) => {
      return !isBillingReadOnly && shouldValidateLocation ? schema.trim().required() : yup.string().notRequired();
    }),
  postalCode: yup
    .string()
    .when(
      ['$isBillingReadOnly', '$siteId', '$shouldValidateLocation'],
      (isBillingReadOnly, siteId, shouldValidateLocation, schema) => {
        if (!isBillingReadOnly && shouldValidateLocation) {
          if (siteId === 'IWBR' || siteId === 'WIBR') {
            return schema.matches(CEP_REGEX);
          }
        }
        return yup.string().notRequired();
      }
    ),
  locationState: yup
    .string()
    .when(['$isBillingReadOnly', '$shouldValidateLocation'], (isBillingReadOnly, shouldValidateLocation, schema) => {
      return !isBillingReadOnly && shouldValidateLocation ? schema.required() : yup.string().notRequired();
    }),
  locationCity: yup
    .string()
    .when(['$isBillingReadOnly', '$shouldValidateLocation'], (isBillingReadOnly, shouldValidateLocation, schema) => {
      return !isBillingReadOnly && shouldValidateLocation ? schema.required() : yup.string().notRequired();
    }),
  locationZone: yup.mixed().when('$shouldValidateLocation', (shouldValidateLocation, schema) => {
    if (shouldValidateLocation) {
      return yup.mixed().notOneOf(['']);
    }
    return schema;
  }),
  locationSubZone: yup.mixed().when('$shouldValidateLocation', (shouldValidateLocation, schema) => {
    if (shouldValidateLocation) {
      return yup.mixed().notOneOf(['']);
    }
    return schema;
  }),
  cardholderName: yup.string().required(),
  email: yup
    .string()
    .email()
    .required(),
  areacode: yup
    .string()
    .trim()
    .max(6)
    .matches(ONLY_NUMBERS)
    .required(),
  phoneNumber: yup
    .string()
    .trim()
    .max(14)
    .matches(ONLY_NUMBERS)
    .required(),
  docType: yup.string().when('$siteId', (siteId, schema) => {
    return siteId !== '24MX' && siteId !== 'VAMX' ? schema.required() : yup.string().notRequired();
  }),
  docNumber: yup.string().when(['$siteId', 'docType'], (siteId, docType, schema) => {
    if (siteId === 'ZPAR') {
      return yup
        .string()
        .trim()
        .min(7)
        .max(24)
        .transform(value => (value ? value.replace(/[\W]/g, '') : value))
        .matches(ONLY_NUMBERS);
    }
    if (siteId === 'IWBR' || siteId === 'WIBR') {
      if (docType.toUpperCase() === 'CNPJ') {
        return schema.matches(CNPJ_REGEX);
      }
      return schema.matches(CPF_REGEX);
    }
    if (siteId === 'DVPE' || siteId === 'UBPE') {
      if (docType === 'RUC') {
        return schema.length(11).matches(ONLY_NUMBERS);
      }
      return schema
        .min(8)
        .max(9)
        .matches(ONLY_NUMBERS);
    }
    if (siteId === 'I24') {
      return yup.string().notRequired();
    }
    return schema;
  }),
  cardNumber: yup
    .string()
    .transform(v => (v ? v.replace(/\s/g, '') : v))
    .min(12)
    .max(19)
    .matches(ONLY_NUMBERS)
    .required(),
  cardExpiration: yup
    .string()
    .matches(CARD_EXPIRATION_REGEX)
    .required(),
  securityCode: yup
    .string()
    .trim()
    .min(3)
    .max(4)
    .matches(ONLY_NUMBERS)
    .required(),
  billingTos: yup
    .boolean()
    .oneOf([true])
    .required()
});

const Step4Module2ContactValidations = yup.object().shape({
  phoneNumber: yup
    .string()
    .trim()
    .max(30)
    .matches(ONLY_NUMBERS)
    .required()
});

export { Step4Module2Validations, Step4Module2ContactValidations };
